import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FiEdit3 } from "react-icons/fi";
import { MdOutlineDelete } from "react-icons/md";
import { AiOutlineEye } from "react-icons/ai";

import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  ExternalContainer,
  InternalContainer,
} from "../../components/Reusable/Layout";
import { GradientButton, Search } from "../../components/forms";
import {
  fetchSupport,
  deleteSupport,
} from "../../features/auth/store/supportSlice";
import { SupportItem } from "../../features/auth/store/types/support-types";
import SupportManagementModal from "./Modal/SupportManagementModal";
import DeleteSupportModal from "./Modal/DeleteSupportModal";
import ViewSupportModal from "./Modal/ViewSupportModal";

const Support = () => {
  const dispatch = useAppDispatch();
  const items = useAppSelector((state: any) => state.support?.items ?? []);
  const loading = useAppSelector(
    (state: any) => state.support?.loading ?? false,
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<SupportItem | null>(null);
  const [search, setSearch] = useState("");

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<SupportItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingItem, setViewingItem] = useState<SupportItem | null>(null);

  useEffect(() => {
    dispatch(fetchSupport());
  }, [dispatch]);

  const filteredItems = items.filter((item: SupportItem) =>
    [item.question, item.answer]
      .join(" ")
      .toLowerCase()
      .includes(search.trim().toLowerCase()),
  );

  const openDeleteModal = (item: SupportItem) => {
    setItemToDelete(item);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      await dispatch(deleteSupport(itemToDelete.id)).unwrap();
      toast.success("Support item deleted successfully");
      setDeleteModalOpen(false);
      setItemToDelete(null);
    } catch (err: any) {
      toast.error(err || "Failed to delete support item");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <ExternalContainer>
      <div className="w-full max-w-6xl min-h-screen">
        <InternalContainer headerText="Support Management">
          <div className="flex flex-col gap-6">
            {/* Action Bar */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="w-full sm:w-72">
                <Search
                  placeholder="Search support items..."
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="w-full sm:w-auto">
                <GradientButton
                  content="Manage Support"
                  onClick={() => {
                    setEditingItem(null);
                    setIsModalOpen(true);
                  }}
                />
              </div>
            </div>

            {/* Table Area Container */}
            <div className="flex flex-col">
              {/* CHANGED: Forced table element to layout strictly with scroll containment and a explicit minimal width anchor */}
              <div className="w-full overflow-x-auto rounded-xl border border-zinc-200/50 dark:border-zinc-800/50">
                <div className="inline-block min-w-[700px] w-full align-middle">
                  <table className="w-full font-Inter cursor-pointer border-collapse table-fixed">
                    <thead>
                      <tr className="bg-gray-100 dark:bg-[#FFFFFF1A] text-gray-600 dark:text-white uppercase text-xs tracking-wider h-[50px]">
                        <th className="w-[8%] px-4 py-3 text-left font-semibold">
                          #
                        </th>
                        <th className="w-[32%] px-4 py-3 text-left font-semibold">
                          Question
                        </th>
                        <th className="w-[42%] px-4 py-3 text-left font-semibold">
                          Answer
                        </th>
                        <th className="w-[18%] px-4 py-3 text-center font-semibold">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-600 dark:text-white text-xs sm:text-sm divide-y divide-gray-200/20">
                      {loading ? (
                        <tr className="h-[120px]">
                          <td colSpan={4} className="px-6 text-center">
                            <div className="flex items-center justify-center">
                              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
                            </div>
                          </td>
                        </tr>
                      ) : filteredItems.length > 0 ? (
                        filteredItems.map(
                          (item: SupportItem, index: number) => (
                            <tr
                              key={item.id}
                              className="hover:bg-gray-50/5 dark:hover:bg-white/5 transition-colors duration-150 h-[60px]"
                            >
                              <td className="px-4 py-4 align-middle text-gray-400 font-medium truncate">
                                {index + 1}
                              </td>
                              <td className="px-4 py-4 align-middle font-medium text-gray-900 dark:text-white truncate">
                                {item.question}
                              </td>
                              <td className="px-4 py-4 align-middle">
                                <div
                                  className="line-clamp-2 text-gray-500 dark:text-gray-400 prose prose-sm max-w-none dark:prose-invert"
                                  dangerouslySetInnerHTML={{
                                    __html: item.answer,
                                  }}
                                />
                              </td>
                              <td className="px-4 py-4 align-middle">
                                {/* CHANGED: Wrapped in an uncollapsible, centered flex row block */}
                                <div className="flex items-center justify-center gap-3 min-w-[90px]">
                                  <button
                                    type="button"
                                    className="text-green-600 hover:text-green-500 active:scale-95 transition-transform p-1"
                                    onClick={() => {
                                      setViewingItem(item);
                                      setViewModalOpen(true);
                                    }}
                                    title="View Support Item"
                                  >
                                    <AiOutlineEye className="text-xl" />
                                  </button>
                                  <button
                                    type="button"
                                    className="text-blue-600 hover:text-blue-500 active:scale-95 transition-transform p-1"
                                    onClick={() => {
                                      setEditingItem(item);
                                      setIsModalOpen(true);
                                    }}
                                    title="Edit Support Item"
                                  >
                                    <FiEdit3 className="text-xl" />
                                  </button>
                                  <button
                                    type="button"
                                    className="text-red-600 hover:text-red-500 active:scale-95 transition-transform p-1"
                                    onClick={() => openDeleteModal(item)}
                                    title="Delete Support Item"
                                  >
                                    <MdOutlineDelete className="text-xl" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ),
                        )
                      ) : (
                        <tr className="h-[80px]">
                          <td
                            colSpan={4}
                            className="px-6 text-center text-gray-400 py-8"
                          >
                            No support items found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </InternalContainer>
      </div>

      {/* Modals downstream */}
      <SupportManagementModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingItem(null);
        }}
        initialEditItem={editingItem}
      />

      <DeleteSupportModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
        question={itemToDelete?.question}
      />

      <ViewSupportModal
        isOpen={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setViewingItem(null);
        }}
        item={viewingItem}
      />
    </ExternalContainer>
  );
};

export default Support;
